import React, { useState, useEffect, useCallback } from "react";
import {
  getQuestions,
  postQuestion,
  postAnswer,
} from "shared/api/function_data";
import "./QnAPanel.css";

export default function QnAPanel({
  isOpen,
  onClose,
  functionId,
  itemKey,
  week,
  year,
  userlogin,
}) {
  const [qnaData, setQnaData] = useState([]);
  const [newQuestion, setNewQuestion] = useState("");
  const [answeringId, setAnsweringId] = useState(null);
  const [newAnswer, setNewAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // ─── Lock body scroll ───
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // ─── Fetch questions ───
  const fetchQuestions = useCallback(async () => {
    if (!functionId || !itemKey || !week || !year) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getQuestions(functionId, itemKey, week, year);
      setQnaData(data);
    } catch (err) {
      setError("Failed to load questions. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [functionId, itemKey, week, year]);

  useEffect(() => {
    if (isOpen) {
      fetchQuestions();
    }
  }, [isOpen, fetchQuestions]);

  // ─── Submit question ───
  const handleSubmitQuestion = async () => {
    const text = newQuestion.trim();
    if (!text || submitting) return;

    setSubmitting(true);
    setError(null);

    const tempId = `temp-q-${Date.now()}`;
    const optimistic = {
      id: tempId,
      question: text,
      author: userlogin,
      timestamp: new Date().toISOString(),
      answers: [],
      _pending: true,
    };
    setQnaData((prev) => [optimistic, ...prev]);
    setNewQuestion("");

    try {
      const saved = await postQuestion(
        functionId,
        itemKey,
        week,
        year,
        userlogin,
        text
      );
      setQnaData((prev) =>
        prev.map((q) => (q.id === tempId ? { ...saved, _pending: false } : q))
      );
    } catch (err) {
      setQnaData((prev) => prev.filter((q) => q.id !== tempId));
      setError("Failed to post question. Please try again.");
      setNewQuestion(text);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Submit answer ───
const handleSubmitAnswer = async (questionId) => {
  const text = newAnswer.trim();
  if (!text || submitting) return;

  setSubmitting(true);
  setError(null);

  const tempId = `temp-a-${Date.now()}`;
  const optimisticAnswer = {
    id: tempId,
    text,
    author: userlogin,
    timestamp: new Date().toISOString(),
    _pending: true,
  };

  setQnaData((prev) =>
    prev.map((q) =>
      q.id === questionId
        ? { ...q, answers: [...q.answers, optimisticAnswer] }
        : q
    )
  );
  setNewAnswer("");
  setAnsweringId(null);

  try {
    // Pass context params so backend does direct file lookup
    const saved = await postAnswer(
      questionId,
      userlogin,
      text,
      functionId,
      itemKey,
      week,
      year
    );
    setQnaData((prev) =>
      prev.map((q) =>
        q.id === questionId
          ? {
              ...q,
              answers: q.answers.map((a) =>
                a.id === tempId ? { ...saved, _pending: false } : a
              ),
            }
          : q
      )
    );
  } catch (err) {
    setQnaData((prev) =>
      prev.map((q) =>
        q.id === questionId
          ? { ...q, answers: q.answers.filter((a) => a.id !== tempId) }
          : q
      )
    );
    setError("Failed to post answer. Please try again.");
    setNewAnswer(text);
    setAnsweringId(questionId);
  } finally {
    setSubmitting(false);
  }
};
  // ─── Helpers ───
  const formatTime = (ts) => {
    try {
      return new Date(ts).toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return ts;
    }
  };

  const avatarLetter = (login) =>
    login ? login.charAt(0).toUpperCase() : "?";

  return (
    <>
      {/* Backdrop */}
      <div
        className={`qna-backdrop ${isOpen ? "qna-backdrop--visible" : ""}`}
        onClick={onClose}
      />

      {/* Panel */}
      <div className={`qna-offcanvas ${isOpen ? "qna-offcanvas--open" : ""}`}>
        {/* Header */}
        <div className="qna-header">
          <div className="qna-header-left">
            <h3 className="qna-title">Q&amp;A</h3>
            <span className="qna-count">{qnaData.length} questions</span>
          </div>
          <button className="qna-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="qna-error-banner">
            <span>{error}</span>
            <button
              className="qna-error-dismiss"
              onClick={() => setError(null)}
            >
              ✕
            </button>
          </div>
        )}

        {/* Body */}
        <div className="qna-body">
          {loading ? (
            <div className="qna-empty">
              <span className="qna-empty-icon">⏳</span>
              <p className="qna-empty-text">Loading questions…</p>
            </div>
          ) : qnaData.length === 0 ? (
            <div className="qna-empty">
              <span className="qna-empty-icon">💬</span>
              <p className="qna-empty-text">
                No questions yet. Be the first to ask!
              </p>
            </div>
          ) : (
            qnaData.map((item) => (
              <div
                key={item.id}
                className={`qna-item ${item._pending ? "qna-item--pending" : ""}`}
              >
                {/* Question */}
                <div className="qna-question">
                  <div className="qna-question-header">
                    <div className="qna-author">
                      <span className="qna-avatar">
                        {avatarLetter(item.author)}
                      </span>
                      <span className="qna-author-login">@{item.author}</span>
                    </div>
                    <span className="qna-time">
                      {formatTime(item.timestamp)}
                    </span>
                  </div>
                  <p className="qna-question-text">{item.question}</p>
                  <div className="qna-question-actions">
                    <span className="qna-answer-count">
                      {item.answers.length}{" "}
                      {item.answers.length === 1 ? "answer" : "answers"}
                    </span>
                    <button
                      className="qna-reply-btn"
                      onClick={() =>
                        setAnsweringId(
                          answeringId === item.id ? null : item.id
                        )
                      }
                      disabled={item._pending}
                    >
                      {answeringId === item.id ? "Cancel" : "Reply"}
                    </button>
                  </div>
                </div>

                {/* Answers */}
                {item.answers.length > 0 && (
                  <div className="qna-answers">
                    {item.answers.map((answer) => (
                      <div
                        key={answer.id}
                        className={`qna-answer ${answer._pending ? "qna-answer--pending" : ""}`}
                      >
                        <div className="qna-answer-header">
                          <div className="qna-author">
                            <span className="qna-avatar qna-avatar--answer">
                              {avatarLetter(answer.author)}
                            </span>
                            <span className="qna-author-login">
                              @{answer.author}
                            </span>
                          </div>
                          <span className="qna-time">
                            {formatTime(answer.timestamp)}
                          </span>
                        </div>
                        <p className="qna-answer-text">{answer.text}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply Input */}
                {answeringId === item.id && (
                  <div className="qna-reply-box">
                    <textarea
                      className="qna-textarea"
                      rows="2"
                      placeholder="Write your answer..."
                      value={newAnswer}
                      onChange={(e) => setNewAnswer(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && e.ctrlKey)
                          handleSubmitAnswer(item.id);
                      }}
                    />
                    <div className="qna-reply-actions">
                      <span className="qna-hint">Ctrl + Enter to submit</span>
                      <button
                        className="qna-submit-btn"
                        onClick={() => handleSubmitAnswer(item.id)}
                        disabled={!newAnswer.trim() || submitting}
                      >
                        {submitting ? "Submitting…" : "Submit"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Sticky Ask Box */}
        <div className="qna-ask">
          <textarea
            className="qna-textarea"
            rows="2"
            placeholder="Ask a question about this report..."
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.ctrlKey) handleSubmitQuestion();
            }}
          />
          <div className="qna-ask-actions">
            <span className="qna-hint">Ctrl + Enter to submit</span>
            <button
              className="qna-submit-btn"
              onClick={handleSubmitQuestion}
              disabled={!newQuestion.trim() || submitting}
            >
              {submitting ? "Posting…" : "Ask Question"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}